import { Card, Table, message } from 'antd';
import { ColumnType } from 'antd/es/table';
import { getSalesManagement, SalesManagementItem } from 'apis/oms';
import dayjs from 'dayjs';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { thousands } from 'utils/tools';
import i18n from 'i18n';
import LOCALS from 'commons/locals';
import { TrendingUp } from 'lucide-react';
import { useAppSelector } from 'store/hooks';
import { selectGlobalInfo } from 'store/slices/globalInfoSlice';
import { PRODUCT_SOURCE_TYPE_OPTION_LIST } from 'constant/pms-product';

export interface SalesManagementTableRef {
  getSheetData: () => Promise<{
    headers: string[];
    rows: (string | number)[][];
  }>;
}

interface SalesManagementTableProps {
  createdFrom?: number;
  loading?: boolean;
  pageSize?: number;
}

const SalesManagementTable = forwardRef<
  SalesManagementTableRef,
  SalesManagementTableProps
>(
  (
    {
      createdFrom,
      loading: parentLoading = false,
      pageSize: initialPageSize = 10,
    },
    ref,
  ) => {
    const [data, setData] = useState<SalesManagementItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [current, setCurrent] = useState(1);
    const [pageSize, setPageSize] = useState(initialPageSize);
    const { staffSelectOptions } = useAppSelector(selectGlobalInfo);

    const receptionistNameMap = useMemo(() => {
      return staffSelectOptions.reduce<Record<number, string>>((acc, cur) => {
        const id = Number(cur.value);
        if (!Number.isNaN(id)) {
          acc[id] = cur.label;
        }
        return acc;
      }, {});
    }, [staffSelectOptions]);

    const getReceptionistName = useCallback(
      (receptionistId: number | null | undefined) => {
        if (receptionistId === null || receptionistId === undefined) return '-';
        return receptionistNameMap[receptionistId] || `${receptionistId}`;
      },
      [receptionistNameMap],
    );

    // 获取商品来源类型的显示文本
    const getSourceTypeLabel = useCallback((sourceType: number) => {
      const option = PRODUCT_SOURCE_TYPE_OPTION_LIST.find(
        (item) => item.value === sourceType,
      );
      if (!option) return '-';
      return option.label;
    }, []);

    // 获取销售管理数据
    const fetchData = useCallback(
      async (page: number) => {
        if (!createdFrom) return;

        setLoading(true);
        // 确保在获取数据前同步更新当前页码状态
        setCurrent(page);
        try {
          const response = await getSalesManagement({
            createdFrom,
            pageNum: page,
            pageSize,
          });
          setData(response.list || []);
          setTotal(response.total || 0);
        } catch (error) {
          console.error('Failed to fetch sales management data:', error);
          message.error('获取销售管理数据失败');
          setData([]);
          setTotal(0);
        } finally {
          setLoading(false);
        }
      },
      [createdFrom, pageSize],
    );

    // 分页切换处理
    const handlePageChange = useCallback(
      (page: number, size?: number) => {
        if (size && size !== pageSize) {
          // 如果页面大小发生变化，更新 pageSize 并重置到第一页
          setPageSize(size);
          // fetchData 会在 useEffect 中被触发，因为 pageSize 变化了，setCurrent 在 fetchData 中处理
        } else {
          // 只是页码变化，fetchData 会处理 setCurrent
          fetchData(page);
        }
      },
      [fetchData, pageSize],
    );

    // 同步外部传入的 pageSize
    useEffect(() => {
      setPageSize(initialPageSize);
    }, [initialPageSize]);

    // 当 createdFrom 或 pageSize 变化时重新获取数据并重置分页
    useEffect(() => {
      if (createdFrom) {
        fetchData(1);
      }
    }, [createdFrom, pageSize, fetchData]);

    // 表格列定义
    const columns: ColumnType<SalesManagementItem>[] = useMemo(
      () => [
        {
          title: i18n.t(LOCALS.sales_date),
          dataIndex: 'deliveryTime',
          key: 'deliveryTime',
          width: 100,
          render: (text: string) =>
            text ? dayjs(text).format('YYYY/MM/DD') : '-',
        },
        {
          title: i18n.t(LOCALS.sales_product_id),
          dataIndex: 'productId',
          key: 'productId',
          width: 120,
          render: (text: string) => text || '-',
        },
        {
          title: i18n.t(LOCALS.product_name),
          dataIndex: 'detailTitle',
          key: 'detailTitle',
          width: 250,
          render: (text: string) => text || '-',
        },
        {
          title: i18n.t(LOCALS.rank),
          dataIndex: 'rank',
          key: 'rank',
          width: 80,
          render: (text: string) => text || '-',
        },
        {
          title: (
            <span style={{ whiteSpace: 'pre-line' }}>
              {i18n.t(LOCALS.purchase_price_tax_inclusive)}
            </span>
          ),
          dataIndex: 'costPrice',
          key: 'costPrice',
          width: 120,
          align: 'right',
          render: (text: string) => thousands(text),
        },
        {
          title: (
            <span style={{ whiteSpace: 'pre-line' }}>
              {i18n.t(LOCALS.selling_price_tax_inclusive)}
            </span>
          ),
          dataIndex: 'price',
          key: 'price',
          width: 120,
          align: 'right',
          render: (text: string) => thousands(text),
        },
        {
          title: i18n.t(LOCALS.purchasing_staff),
          dataIndex: 'recycleStaffName',
          key: 'recycleStaffName',
          width: 100,
          render: (text: string) => text || '-',
        },
        {
          title: i18n.t(LOCALS.reception_staff),
          dataIndex: 'receptionistId',
          key: 'receptionistId',
          width: 120,
          render: (
            _: number,
            record: { receptionistId: number | null | undefined },
          ) => getReceptionistName(record?.receptionistId),
        },
        {
          title: i18n.t(LOCALS.sales_staff),
          dataIndex: 'staffName',
          key: 'staffName',
          width: 100,
          render: (text: string) => text || '-',
        },
        {
          title: i18n.t(LOCALS.juusvCbRWo),
          dataIndex: 'entryTime',
          key: 'entryTime',
          width: 160,
          render: (text: string) =>
            text ? dayjs(text).format('YYYY/MM/DD HH:mm:ss') : '-',
        },

        {
          title: (
            <span style={{ whiteSpace: 'pre-line' }}>
              {i18n.t(LOCALS.purchase_price_tax_free)}
            </span>
          ),
          dataIndex: 'costPriceWithoutTax',
          key: 'costPriceWithoutTax',
          width: 120,
          align: 'right',
          render: (text: string) => thousands(text),
        },

        {
          title: (
            <span style={{ whiteSpace: 'pre-line' }}>
              {i18n.t(LOCALS.selling_price_tax_free)}
            </span>
          ),
          dataIndex: 'priceWithoutTax',
          key: 'priceWithoutTax',
          width: 120,
          align: 'right',
          render: (text: string) => thousands(text),
        },
        {
          title: (
            <span style={{ whiteSpace: 'pre-line' }}>
              {i18n.t(LOCALS.gross_profit_tax_free)}
            </span>
          ),
          dataIndex: 'grossProfitWithoutTax',
          key: 'grossProfitWithoutTax',
          width: 120,
          align: 'right',
          render: (text: string) => thousands(text),
        },

        {
          title: i18n.t(LOCALS.points_earned),
          dataIndex: 'integration',
          key: 'integration',
          width: 140,
          align: 'right',
          render: (text: string) => thousands(text),
        },
        {
          title: i18n.t(LOCALS.used_points),
          dataIndex: 'integrationAmount',
          key: 'integrationAmount',
          width: 140,
          align: 'right',
          render: (text: string) => {
            const num = parseFloat(text);
            return isNaN(num) || num === 0 ? '' : thousands(text);
          },
        },
        {
          title: i18n.t(LOCALS.sns_discount),
          key: 'snsDiscount',
          width: 160,
          align: 'right',
          render: () => '-', // 当前接口暂无此字段
        },
        {
          title: i18n.t(LOCALS.discount_amounts),
          dataIndex: 'promotionAmountActualCurrency',
          key: 'promotionAmountActualCurrency',
          width: 120,
          align: 'right',
          render: (text: string | null) => {
            if (!text) return '-';
            const num = Number(text);
            return isNaN(num) ? text : thousands(text);
          },
        },
        {
          title: i18n.t(LOCALS.product_source),
          dataIndex: 'sourceType',
          key: 'sourceType',
          width: 100,
          render: (sourceType: number) => getSourceTypeLabel(sourceType),
        },
        {
          title: i18n.t(LOCALS.sales_notes),
          dataIndex: 'note',
          key: 'note',
          width: 160,
          render: (note: string | null) => note || '-',
        },
      ],
      [getSourceTypeLabel, getReceptionistName],
    );

    useImperativeHandle(ref, () => ({
      getSheetData: async () => {
        const allData = await getSalesManagement({
          createdFrom,
          pageNum: 1,
          // TODO: 需要优化，不能一次性获取所有数据，接口应该支持按照日期筛选
          pageSize: 10000,
        });

        const headers = [
          i18n.t(LOCALS.sales_date),
          i18n.t(LOCALS.sales_product_id),
          i18n.t(LOCALS.product_name),
          i18n.t(LOCALS.rank),
          `${i18n.t(LOCALS.purchase_price)}（${i18n.t(LOCALS.tax_inclusive)}）`,
          `${i18n.t(LOCALS.selling_price)}（${i18n.t(LOCALS.tax_inclusive)}）`,
          i18n.t(LOCALS.purchasing_staff),
          i18n.t(LOCALS.reception_staff),
          i18n.t(LOCALS.sales_staff),
          i18n.t(LOCALS.juusvCbRWo),
          `${i18n.t(LOCALS.purchase_price)}（${i18n.t(LOCALS.tax_free)}）`,
          `${i18n.t(LOCALS.selling_price)}（${i18n.t(LOCALS.tax_free)}）`,
          `${i18n.t(LOCALS.gross_profit)}（${i18n.t(LOCALS.tax_free)}）`,
          i18n.t(LOCALS.points_earned),
          i18n.t(LOCALS.used_points),
          i18n.t(LOCALS.sns_discount),
          i18n.t(LOCALS.discount_amounts),
          i18n.t(LOCALS.product_source),
          i18n.t(LOCALS.sales_notes),
        ];
        const rows = allData.list.map((d) => [
          // 売却日
          d.deliveryTime ? dayjs(d.deliveryTime).format('YYYY/MM/DD') : '-',
          // 商品コード
          d.productId || '-',
          // 商品名（含 detailTitle）
          d.detailTitle || '-',
          // ランク
          d.rank || '-',
          // 仕入額（税込）
          thousands(d.costPrice),
          // 販売額（税込）
          thousands(d.price),
          // 買取担当
          d.recycleStaffName || '-',
          // 受付担当
          getReceptionistName(d.receptionistId),
          // 売却担当
          d.staffName || '-',
          // 入庫時間
          d.entryTime ? dayjs(d.entryTime).format('YYYY/MM/DD HH:mm:ss') : '-',
          // 仕入額（税抜）
          thousands(d.costPriceWithoutTax),
          // 販売額（税抜）
          thousands(d.priceWithoutTax),
          // 粗利（税抜）
          thousands(d.grossProfitWithoutTax),
          // 付与ポイント
          thousands(d.integration ?? undefined),
          // 使用ポイント
          (() => {
            const num = parseFloat(d.integrationAmount);
            return isNaN(num) || num === 0
              ? ''
              : thousands(d.integrationAmount);
          })(),
          // SNS登録者引き（暂无字段）
          '-',
          // 値引き
          (() => {
            const val = d.promotionAmountActualCurrency;
            if (!val) return '-';
            const num = Number(val);
            return isNaN(num) ? val : thousands(val);
          })(),
          // 仕入元
          getSourceTypeLabel(d.sourceType),
          // メモ
          d.note || '-',
        ]);
        return { headers, rows };
      },
    }));

    return (
      <Card bodyStyle={{ padding: '16px' }}>
        <div className="flex items-center gap-2 mb-4 text-base font-medium">
          <TrendingUp className="w-5 h-5" />
          <div className="text-base font-medium">
            {i18n.t('sales_management_table')}
          </div>
        </div>
        <Table
          columns={columns}
          dataSource={data}
          rowKey={(record) => `${record.orderId}-${record.productId}`}
          loading={loading || parentLoading}
          scroll={{ x: 'max-content' }}
          bordered
          tableLayout="auto"
          pagination={{
            current,
            pageSize,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} / ${total} ${i18n.t(LOCALS.item)}`,
            onChange: handlePageChange,
            onShowSizeChange: handlePageChange,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
        />
      </Card>
    );
  },
);

export default SalesManagementTable;
